import axiosInstance from '../../../axiosInstance';
import apiEndpoints from '../../../api';
import { notify } from '../../../../utils/notification';

// Member self-service reads, against the /api/member/* namespace.
//
// These are not the admin finance endpoints with a flag on them: they are their
// own routes carrying no admin permission. Authorization is membership of the
// society plus a hard scope to the flats the caller owns or occupies, resolved
// server-side from the JWT. Identity is never accepted as input — a flat_id is
// a filter that can narrow a result, never widen it — and asking for another
// flat's document comes back 404 rather than 403.
//
// Nothing here mutates finance data. The resident complaint flow (list, raise,
// attach, comment, rate) lives at the bottom of this file, on
// /api/member/complaints*, so a role holding only member_portal.complaints
// never touches the staff complaint endpoints. Notices keep the Notice slice.

const clean = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
};

// The society is carried by the X-Society-Id header the axios interceptor adds,
// but sending it explicitly keeps these calls readable in the network log and
// matches the rest of the request layer.
const params = (data, extra = {}) => clean({ society_id: data?.societyId, ...extra });

// Tiles on the overview: outstanding, oldest due, paid, open complaints.
export const getMemberSummaryReq = (action) => {
    return axiosInstance.get(apiEndpoints.MEMBER_SUMMARY_API, { params: params(action.data) })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load your summary');
            return err;
        });
};

// The flats this member owns or occupies — the flat strip on the overview and
// the flat filter on the bills screen for multi-flat owners.
export const getMemberFlatsReq = (action) => {
    return axiosInstance.get(apiEndpoints.MEMBER_FLATS_API, { params: params(action.data) })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load your flats');
            return err;
        });
};

export const getMemberInvoicesReq = (action) => {
    return axiosInstance.get(apiEndpoints.MEMBER_INVOICES_API, {
        params: params(action.data, {
            page: action.data?.page || 1,
            limit: action.data?.limit || 10,
            flat_id: action.data?.flatId,
            status: action.data?.status,
            from_date: action.data?.fromDate,
            to_date: action.data?.toDate,
        }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load your bills');
            return err;
        });
};

export const getMemberInvoiceDetailsReq = (action) => {
    const url = apiEndpoints.MEMBER_INVOICE_DETAILS_API.replace(':id', action.data?.id);
    return axiosInstance.get(url, { params: params(action.data) })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load this bill');
            return err;
        });
};

export const getMemberPaymentsReq = (action) => {
    return axiosInstance.get(apiEndpoints.MEMBER_PAYMENTS_API, {
        params: params(action.data, {
            page: action.data?.page || 1,
            limit: action.data?.limit || 10,
            flat_id: action.data?.flatId,
            from_date: action.data?.fromDate,
            to_date: action.data?.toDate,
        }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load your payments');
            return err;
        });
};

// Payment detail carries the invoice allocations, so the screen can show
// "Rs 8,900 -> INV/2026-27/0356" without a second call.
export const getMemberPaymentDetailsReq = (action) => {
    const url = apiEndpoints.MEMBER_PAYMENT_DETAILS_API.replace(':id', action.data?.id);
    return axiosInstance.get(url, { params: params(action.data) })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load this receipt');
            return err;
        });
};

// Statement of account for a window — a financial year, or an explicit
// from/to. Never paginated: the running balance only means anything if the
// whole window comes back in one piece, opening balance included.
export const getMemberLedgerReq = (action) => {
    // The window is named one way or the other, never both: the endpoint rejects
    // a financial_year sent alongside an explicit from/to range. A financial year
    // wins when given, and the dates are dropped rather than sent to be refused.
    const financialYear = action.data?.financialYear;
    const window = financialYear
        ? { financial_year: financialYear }
        : { from_date: action.data?.fromDate, to_date: action.data?.toDate };

    return axiosInstance.get(apiEndpoints.MEMBER_LEDGER_API, {
        params: params(action.data, { ...window, flat_id: action.data?.flatId }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load your statement');
            return err;
        });
};

/* ── Complaints ─────────────────────────────────────────────────────────── */

const withId = (template, id) => template.replace(':id', id);

export const getMemberComplaintsReq = (action) => {
    return axiosInstance.get(apiEndpoints.MEMBER_COMPLAINTS_API, {
        params: params(action.data, {
            page: action.data?.page || 1,
            limit: action.data?.limit || 10,
            status: action.data?.status,
            flat_id: action.data?.flatId,
            search: action.data?.search,
        }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load your complaints');
            return err;
        });
};

export const getMemberComplaintDetailsReq = (action) => {
    return axiosInstance.get(withId(apiEndpoints.MEMBER_COMPLAINT_DETAILS_API, action.data?.id), {
        params: params(action.data),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load this complaint');
            return err;
        });
};

export const getMemberComplaintEnumsReq = (action) => {
    return axiosInstance.get(apiEndpoints.MEMBER_COMPLAINT_ENUMS_API, { params: params(action?.data) })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load complaint options');
            return err;
        });
};

// Identity (society, raiser) comes from the token; the body carries only what
// the member chose. society_id rides as a query param for the request log.
export const createMemberComplaintReq = (action) => {
    const { onSuccess, onError, societyId, ...body } = action.data || {};
    return axiosInstance.post(apiEndpoints.MEMBER_COMPLAINTS_API, clean(body), {
        params: params({ societyId }),
    })
        .then(res => {
            if (res?.success) notify('success', res.message || 'Complaint raised');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to raise complaint');
            return err;
        });
};

export const uploadMemberComplaintAttachmentsReq = (action) => {
    const formData = new FormData();
    (action.data?.files || []).forEach((f) => formData.append('files', f));
    return axiosInstance.post(withId(apiEndpoints.MEMBER_COMPLAINT_ATTACHMENTS_API, action.data?.id), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
        .then(res => {
            if (res?.success) notify('success', res.message || 'Attachments uploaded');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to upload attachments');
            return err;
        });
};

export const addMemberComplaintCommentReq = (action) => {
    const { id, files, comment_text } = action.data || {};
    let body = clean({ comment_text });
    if (Array.isArray(files) && files.length > 0) {
        const formData = new FormData();
        if (comment_text) formData.append('comment_text', comment_text);
        files.forEach((f) => formData.append('files', f));
        body = formData;
    }
    return axiosInstance.post(withId(apiEndpoints.MEMBER_COMPLAINT_COMMENTS_API, id), body)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Comment added');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to add comment');
            return err;
        });
};

export const rateMemberComplaintReq = (action) => {
    const { id, rating, feedback } = action.data || {};
    return axiosInstance.patch(withId(apiEndpoints.MEMBER_RATE_COMPLAINT_API, id), clean({ rating, feedback }))
        .then(res => {
            if (res?.success) notify('success', res.message || 'Thanks for the feedback');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to submit rating');
            return err;
        });
};
